import { Check, ChevronRight, Copy, Globe, Rocket } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { HeroWorkflowFlowPreview } from "components/landing/hero/HeroWorkflowFlowPreview";
import { Section } from "components/landing/layout/Section";
import { Button } from "components/products/muggleTestV0/components/ui/button";
import {
  AGENT_CAPABILITIES,
  HERO_CTA_PRIMARY,
  HERO_WORKFLOW_FLOW,
} from "content/landing";
import { useLandingAuthActions } from "hooks/landing/useLandingAuthActions";
import { useSectionViewTracking } from "lib/analytics";
import { cn } from "products/muggleTestV0/lib/utils";

const MUGGLE_TEST_SLASH_COMMAND = "/muggle-test";

const CLI_TABS = [
  {
    id: "claude-code",
    label: "Claude Code",
    lines: [
      { prompt: ">", text: "/plugin marketplace add https://github.com/multiplex-ai/muggle-ai-works" },
      { prompt: ">", text: "/plugin install muggleai@muggle-works" },
    ],
    copyText: "/plugin marketplace add https://github.com/multiplex-ai/muggle-ai-works\n/plugin install muggleai@muggle-works",
    testCommand: MUGGLE_TEST_SLASH_COMMAND,
  },
  {
    id: "codex",
    label: "Codex",
    lines: [
      { prompt: "$", text: "codex plugin marketplace add multiplex-ai/muggle-ai-works" },
      { prompt: "$", text: "codex plugin add muggle@muggle-works" },
    ],
    copyText: "codex plugin marketplace add multiplex-ai/muggle-ai-works\ncodex plugin add muggle@muggle-works",
    // Codex has no plugin slash commands; it invokes skills by `$plugin:skill` mention.
    testCommand: "$muggle:muggle-test",
  },
  {
    id: "cursor",
    label: "Cursor",
    lines: [
      { prompt: "$", text: "npm i -g @muggleai/works" },
    ],
    copyText: "npm i -g @muggleai/works",
    docsLink: "/muggleTestV0/docs/getting-started/mcp-quickstart",
    testCommand: MUGGLE_TEST_SLASH_COMMAND,
  },
  {
    id: "npm",
    label: "npm",
    lines: [
      { prompt: "$", text: "npm i -g @muggleai/works" },
      { prompt: "$", text: "muggle serve" },
    ],
    copyText: "npm i -g @muggleai/works\nmuggle serve",
    testCommand: MUGGLE_TEST_SLASH_COMMAND,
  },
];

const CLI_TAB_ROTATE_MS = 3000;

export function HeroSection() {
  const { signup } = useLandingAuthActions();
  const sectionRef = useRef<HTMLDivElement>(null);
  useSectionViewTracking("hero", sectionRef);

  // CLI tab auto-rotation
  const [activeTab, setActiveTab] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % CLI_TABS.length);
    }, CLI_TAB_ROTATE_MS);
    return () => clearInterval(timer);
  }, [isPaused]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(CLI_TABS[activeTab].copyText);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 2000);
  }, [activeTab]);

  const currentTab = CLI_TABS[activeTab];

  return (
    <Section
      id="overview"
      className={cn(
        "relative overflow-hidden bg-gradient-to-b from-background to-muted/25",
        "before:pointer-events-none before:absolute before:inset-0 before:z-0 before:bg-[radial-gradient(ellipse_85%_55%_at_100%_-10%,hsl(var(--accent)/0.11),transparent_55%)]",
        "after:pointer-events-none after:absolute after:inset-0 after:z-0 after:bg-[radial-gradient(ellipse_60%_40%_at_0%_100%,hsl(var(--muted)/0.5),transparent_50%)]",
      )}
    >
      <div
        ref={sectionRef}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-2 pb-6 sm:px-6 sm:pt-3 sm:pb-8 lg:px-8 lg:pt-4 lg:pb-10"
      >
        <div
          className={cn(
            "grid grid-cols-1 gap-6 sm:gap-8",
            "[grid-template-areas:'hero-copy'_'hero-visual'_'hero-actions']",
            "lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-center lg:gap-x-6 lg:gap-y-6 xl:gap-x-8",
            "lg:[grid-template-areas:'hero-copy_hero-visual'_'hero-actions_hero-actions']",
          )}
        >
          <div className="[grid-area:hero-copy]" data-landing-scroll-anchor>
            <header
              className={cn(
                "flex max-w-2xl flex-col",
                "animate-in fade-in-0 slide-in-from-bottom-3 duration-700 motion-reduce:animate-none",
              )}
            >
            <div className="mb-3 inline-flex w-fit items-center rounded-full border border-border bg-muted/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur-sm sm:mb-4">
              Muggle Test
            </div>

            <h1 className="mb-3 max-w-[22rem] text-pretty text-3xl font-bold leading-[1.08] tracking-tight text-foreground sm:mb-4 sm:max-w-xl sm:text-4xl lg:mb-5 lg:max-w-2xl lg:text-5xl">
              An acceptance test agent that keeps your{" "}
              <span className="text-accent whitespace-nowrap">AI agent team</span> building to your{" "}
              <span className="text-accent">taste</span>.
            </h1>

            <p className="mb-4 text-pretty text-xl font-medium leading-snug text-muted-foreground sm:mb-5 sm:text-2xl lg:mb-6">
              Get end-to-end test results in minutes, not weeks.
            </p>

            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center rounded-full border border-accent/20 bg-accent/5 px-3 py-1 text-sm font-semibold uppercase tracking-wider text-accent">
                Quick start
              </div>

              {/* ── Vibe code path ── */}
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="h-5 w-5 shrink-0 text-accent" />
                <span className="text-base font-bold text-foreground">Vibe code with Muggle Test</span>
              </div>

              {/* ── Platform tabs ── */}
              <div
                className="flex flex-wrap gap-2 mb-2"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => { if (!hasClicked) setIsPaused(false); }}
              >
                {CLI_TABS.map((tab, i) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => { setActiveTab(i); setIsPaused(true); setHasClicked(true); }}
                    onMouseEnter={() => { setActiveTab(i); setIsPaused(true); }}
                    className={cn(
                      "rounded-full border px-4 py-1.5 text-sm font-semibold transition-all",
                      i === activeTab
                        ? "border-accent bg-accent text-accent-foreground shadow-sm"
                        : "border-border bg-background text-muted-foreground hover:border-accent/50 hover:text-foreground",
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* ── Terminal card ── */}
              <div className="overflow-hidden rounded-lg border border-border/60 bg-zinc-950 dark:bg-zinc-900">
                <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-1.5">
                  <span className="h-2 w-2 rounded-full bg-red-500/70" />
                  <span className="h-2 w-2 rounded-full bg-yellow-500/70" />
                  <span className="h-2 w-2 rounded-full bg-green-500/70" />
                </div>
                <div className="px-3 py-2.5">
                  <div className="h-[2.75rem] overflow-hidden">
                    {currentTab.lines.map((line, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <code className={cn(
                          "flex-1 min-w-0 truncate text-[13px] font-mono leading-[22px]",
                          "dim" in line && line.dim ? "text-zinc-500" : "text-zinc-300",
                        )}>
                          <span className="text-zinc-500">{line.prompt}</span>{" "}
                          {"dim" in line && line.dim && "docsLink" in currentTab && currentTab.docsLink ? (
                            <Link to={currentTab.docsLink} className="underline decoration-zinc-600 hover:text-zinc-300 transition-colors">
                              {line.text}
                            </Link>
                          ) : (
                            line.text
                          )}
                        </code>
                        {i === 0 && (
                          <button
                            type="button"
                            onClick={handleCopy}
                            className="shrink-0 rounded p-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
                            aria-label="Copy install commands"
                          >
                            {hasCopied
                              ? <Check className="h-3.5 w-3.5 text-green-400" />
                              : <Copy className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center border-t border-white/5 mt-1 pt-1">
                    <code className="text-[13px] font-mono leading-[22px] text-zinc-300">
                      <span className="text-zinc-500">&gt;</span> {currentTab.testCommand} <span className="text-zinc-500">&quot;Logged in users can check their project&apos;s test report.&quot;</span>
                    </code>
                  </div>
                </div>
              </div>

              {/* ── Divider ── */}
              <div className="my-2 flex items-center gap-3">
                <div className="h-px flex-1 border-t border-dashed border-foreground/10" />
                <span className="text-sm font-medium text-muted-foreground/60">or</span>
                <div className="h-px flex-1 border-t border-dashed border-foreground/10" />
              </div>

              {/* ── Web path ── */}
              <div className="flex items-center gap-2 mb-2">
                <Globe className="h-5 w-5 shrink-0 text-accent" />
                <span className="text-base font-bold text-foreground">Get your test report on Muggle Test</span>
              </div>

              <div className="overflow-hidden rounded-lg border border-border/60 bg-muted/40">
                <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                  <p className="text-sm text-muted-foreground sm:text-base">
                    Paste your preview URL — AI agents discover and test user journeys.
                  </p>
                  <Link
                    to="/muggleTestV0/dashboard/projects/create/website"
                    className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-accent hover:text-accent/80 transition-colors sm:text-base"
                  >
                    Test a website
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </header>
          </div>

          <div
            className={cn(
              "[grid-area:hero-visual] w-full max-w-2xl justify-self-center sm:max-w-3xl",
              "lg:flex lg:max-w-none lg:items-center lg:justify-self-stretch",
              "animate-in fade-in-0 slide-in-from-bottom-4 duration-700 motion-reduce:animate-none",
              "delay-100 motion-reduce:delay-0",
            )}
          >
            <HeroWorkflowFlowPreview content={HERO_WORKFLOW_FLOW} />
          </div>

          <div
            className={cn(
              "[grid-area:hero-actions] flex flex-col items-center gap-2.5 lg:col-span-2",
              "animate-in fade-in-0 slide-in-from-bottom-3 duration-700 motion-reduce:animate-none",
              "delay-150 motion-reduce:delay-0",
            )}
          >
            <Button
              size="lg"
              className="h-12 w-full gap-2 bg-primary text-primary-foreground shadow-md hover:bg-primary/90 sm:h-11 sm:w-auto sm:min-w-[9.5rem]"
              onClick={() => signup("hero_primary")}
            >
              {HERO_CTA_PRIMARY}
              <ChevronRight className="h-4 w-4" />
            </Button>

            <section
              aria-labelledby="agent-capabilities-heading"
              className="mt-2 flex flex-col items-center gap-1.5"
            >
              <h2
                id="agent-capabilities-heading"
                className="text-[10px] font-semibold uppercase tracking-[0.18em] text-accent/80 sm:text-xs sm:tracking-[0.22em]"
              >
                Beyond click-and-type — agents also handle
              </h2>
              <ul className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
                {AGENT_CAPABILITIES.map((capability) => {
                  const Icon = capability.icon;
                  return (
                    <li
                      key={capability.shortLabel}
                      className="inline-flex items-center gap-1.5 rounded-full border border-accent/20 bg-accent/[0.06] px-3 py-1.5 text-xs font-semibold text-foreground sm:text-sm"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-accent" aria-hidden={true} />
                      <span>{capability.shortLabel}</span>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
        </div>
      </div>
    </Section>
  );
}
